import {
  ActionIcon,
  Badge,
  Box,
  CopyButton,
  Group,
  Paper,
  Stack,
  Text,
  Tooltip,
  type MantineSize,
  type PaperProps,
} from '@mantine/core';
import { IconCheck, IconChevronRight, IconCopy } from '@tabler/icons-react';
import type { ReactNode } from 'react';

/** Height shared by the side by side explorer columns, so they fill the page without it scrolling */
export const COLUMN_HEIGHT = 'max(480px, calc(100vh - 380px))';

type ExplorerPanelProps = Omit<PaperProps, 'children'> & {
  title: ReactNode;
  /** Dimmed text shown after the title */
  description?: ReactNode;
  /** Controls shown next to the title */
  controls?: ReactNode;
  /** Buttons at the right of the header */
  actions?: ReactNode;
  /** A second header row, for options that do not fit beside the title */
  toolbar?: ReactNode;
  children: ReactNode;
};

/** A bordered column with a header, whose content fills the remaining height */
export function ExplorerPanel({
  title,
  description,
  controls,
  actions,
  toolbar,
  children,
  style,
  ...paperProps
}: ExplorerPanelProps) {
  return (
    <Paper withBorder {...paperProps} style={[{ display: 'flex', flexDirection: 'column' }, style]}>
      <Stack gap={6} p="sm" className="module-explorer-header">
        <Group justify="space-between" gap="xs" wrap="nowrap">
          <Group gap="sm" wrap="nowrap">
            <Text fw={600}>
              {title}
              {description && (
                <Text span size="sm" c="dimmed" fw={400} ml="xs">
                  {description}
                </Text>
              )}
            </Text>
            {controls}
          </Group>
          {actions && (
            <Group gap={4} wrap="nowrap">
              {actions}
            </Group>
          )}
        </Group>
        {toolbar}
      </Stack>
      {children}
    </Paper>
  );
}

export function MethodBadge({ method, size }: { method: string; size?: MantineSize }) {
  return (
    <Badge
      variant="light"
      color={method === 'get' ? 'blue' : method === 'delete' ? 'red' : 'green'}
      radius="sm"
      size={size}
    >
      {method.toUpperCase()}
    </Badge>
  );
}

export function CopyIconButton({ value, label }: { value: string; label: string }) {
  return (
    <CopyButton value={value}>
      {({ copied, copy }) => (
        <Tooltip label={copied ? 'Copied' : label}>
          <ActionIcon variant="subtle" color={copied ? 'teal' : 'gray'} onClick={copy} aria-label={label}>
            {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
          </ActionIcon>
        </Tooltip>
      )}
    </CopyButton>
  );
}

type TreeNodeToggleProps = {
  hasChildren: boolean;
  expanded: boolean;
  /** Handles clicks on the chevron itself. Without it, clicks fall through to the row. */
  onToggle?: () => void;
};

/** The expand/collapse chevron at the start of a tree row, or a spacer of the same width for leaves */
export function TreeNodeToggle({ hasChildren, expanded, onToggle }: TreeNodeToggleProps) {
  if (!hasChildren) return <Box w={18} style={{ flexShrink: 0 }} />;

  return (
    <ActionIcon
      variant="subtle"
      color="gray"
      size="xs"
      aria-label={expanded ? 'Collapse' : 'Expand'}
      onClick={
        onToggle &&
        ((event) => {
          event.stopPropagation();
          onToggle();
        })
      }
    >
      <IconChevronRight
        size={14}
        style={{ transform: expanded ? 'rotate(90deg)' : undefined, transition: 'transform 100ms' }}
      />
    </ActionIcon>
  );
}
